/**
 * Finished Product report (SPEC §8.7).
 *
 * Reading order is the four spec trends — Brix, Brix/acid ratio, titratable
 * acidity, USDA color — then the samples behind the selected week. Each trend
 * panel is the weekly average as a line over the week's min–max range as a
 * band, with that spec's n in the tooltip.
 *
 * The load-bearing rule, restated where the pixels are drawn: a sample missing
 * a spec is excluded from that spec's statistics, never counted as zero. The
 * backend enforces it (nulls skip polars aggregation); this component's part is
 * to plot a valueless week as a *gap* and a missing detail cell as a dash, so
 * the exclusion stays visible rather than smoothed over.
 *
 * Like CAR and RFT, every panel comes from one payload — there are no fetches
 * here, no dependency arrays, and the selected week arrives from the server
 * (`selection`), already resolved against the scope's data.
 */

import type { CustomReportProps } from '..'
import { Card } from '../shared/Card'
import { count, dataset, text } from '../shared/rows'

import '../report.css'

import { SampleTable } from './SampleTable'
import type { WeekStat } from './SpecTrendChart'
import { SpecTrendChart } from './SpecTrendChart'
import { weekLabel } from './format'

const ALL = '(all)'

/** The four panels, in the owner's reading order, at each spec's precision. */
const SPEC_LEGEND =
  'Dashed green is the target, dashed red the spec limits; a red triangle marks a week with out-of-spec samples.'

const PANELS = [
  {
    key: 'brix',
    title: 'Brix, weekly',
    subtitle: `Weekly average (line) over the min–max range (band). ${SPEC_LEGEND}`,
    decimals: 2,
  },
  {
    key: 'ratio',
    title: 'Brix/acid ratio, weekly',
    subtitle: `Samples with no ratio recorded are excluded, never counted as zero. ${SPEC_LEGEND}`,
    decimals: 2,
  },
  {
    key: 'acid',
    title: 'Titratable acidity (as citric), weekly',
    subtitle: `Samples with no acidity recorded are excluded, never counted as zero. ${SPEC_LEGEND}`,
    decimals: 3,
  },
  {
    key: 'color',
    title: 'USDA color, weekly',
    subtitle:
      'Weekly average (line) over the min–max range (band). No spec band is defined for color.',
    decimals: 1,
  },
  {
    key: 'pulp',
    title: 'Pulp, weekly',
    subtitle: `Samples with no pulp recorded are excluded, never counted as zero. ${SPEC_LEGEND}`,
    decimals: 2,
  },
] as const

function asString(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function stat(value: unknown): number | null {
  return typeof value === 'number' ? value : null
}

export function FinishedProduct({ result, values, setParam }: CustomReportProps) {
  const weekly = dataset(result, 'weekly')
  const detail = dataset(result, 'primary')

  // The selected week comes from the server, not from a local fallback — the
  // server already resolved it to compute the detail rows.
  const selectedWeek = text(dataset(result, 'selection')[0]?.week ?? null)

  const site = asString(values.site)
  const formula = asString(values.formula)
  const product = asString(values.product)
  const totalSamples = weekly.reduce((sum, row) => sum + count(row.samples), 0)
  const scopeLabel = product === '' || product === ALL ? 'all products' : `product ${product}`

  const perSpec = (key: string): WeekStat[] =>
    weekly.map((row) => ({
      week: text(row.week) ?? '',
      n: count(row[`${key}_n`]),
      avg: stat(row[`${key}_avg`]),
      min: stat(row[`${key}_min`]),
      max: stat(row[`${key}_max`]),
      // Absent for color (the view defines no color band) — `stat` reads the
      // missing columns as null and the chart draws no overlay.
      lo: stat(row[`${key}_lo`]),
      hi: stat(row[`${key}_hi`]),
      tgt: stat(row[`${key}_tgt`]),
      oos: count(row[`${key}_oos`]),
    }))

  const detailSubtitle =
    selectedWeek === null
      ? 'No week to show'
      : `Week of ${weekLabel(selectedWeek)} · ${detail.length.toString()} ${
          detail.length === 1 ? 'sample' : 'samples'
        }`

  return (
    <div className="bespoke-report">
      <p className="report-summary">
        {site === '' ? '' : `${site} · `}
        {formula === '' ? '' : `${formula} · `}
        {scopeLabel} · {totalSamples.toLocaleString()}{' '}
        {totalSamples === 1 ? 'sample' : 'samples'} across {weekly.length.toString()}{' '}
        {weekly.length === 1 ? 'week' : 'weeks'}
      </p>

      {PANELS.map((panel) => (
        <Card
          key={panel.key}
          title={panel.title}
          subtitle={`${panel.subtitle} Select a week to load its samples below.`}
        >
          <SpecTrendChart
            data={perSpec(panel.key)}
            decimals={panel.decimals}
            selectedWeek={selectedWeek}
            onSelectWeek={(week) => {
              setParam('week', week)
            }}
          />
        </Card>
      ))}

      <Card title="Sample detail" subtitle={detailSubtitle}>
        <SampleTable rows={detail} />
      </Card>
    </div>
  )
}
